import React, { useState, useEffect } from "react";
import { IEvent, IShow, IUkcRallyObedienceAwards } from "@eagerdog/interfaces";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";
import { calculateUkcRallyObedienceAwards } from "@eagerdog/awards-util";

import "./UkcRallyObedienceAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: any[];
}

const UkcRallyObedienceAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IUkcRallyObedienceAwards | null>(null);

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      // Filter scores for this show
      const showScores = scores.filter(
        (s) =>
          s.show_type === show.show_type &&
          s.show_id.toString() === show._id.toString()
      );

      const calculatedAwards = calculateUkcRallyObedienceAwards(showScores);
      setAwards(calculatedAwards);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: number): string => {
    if (!time || time === Number.POSITIVE_INFINITY) return 'N/A';
    return time.toString();
  };

  if (loading) {
    return <div className="UkcRallyObedienceAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="UkcRallyObedienceAwards error">Failed to calculate awards</div>;
  }

  return (
    <div className="UkcRallyObedienceAwards">
      {/* High in Trial */}
      <div className="awardSection">
        <h4>High in Trial</h4>
        {awards.highInTrial ? (
          <>
            <p className="awardWinner">
              #{awards.highInTrial.arm_number} {awards.highInTrial.registered_name} ({awards.highInTrial.breed}) – 
              {awards.highInTrial.level} – Score: {awards.highInTrial.score} – Time: {formatTime(awards.highInTrial.time)}
            </p>
            {(awards.highInTrial.owner_name || awards.highInTrial.handler_name) && (
              <p className="awardDetail">
                {awards.highInTrial.owner_name && `Owner: ${awards.highInTrial.owner_name}`}
                {awards.highInTrial.owner_name && awards.highInTrial.handler_name && ' | '}
                {awards.highInTrial.handler_name && `Handler: ${awards.highInTrial.handler_name}`}
              </p>
            )}
          </>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>

      {/* High in Trial Junior */}
      <div className="awardSection">
        <h4>High in Trial Junior</h4>
        {awards.highInTrialJunior ? (
          <>
            <p className="awardWinner">
              #{awards.highInTrialJunior.arm_number} {awards.highInTrialJunior.registered_name} ({awards.highInTrialJunior.breed}) – 
              {awards.highInTrialJunior.level} – Score: {awards.highInTrialJunior.score} – Time: {formatTime(awards.highInTrialJunior.time)}
            </p>
            {awards.highInTrialJunior.junior_number && (
              <p className="awardDetail">
                Junior Number: {awards.highInTrialJunior.junior_number}
              </p>
            )}
          </>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>
    </div>
  );
};

export default UkcRallyObedienceAwards;

